import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { DemoStep, DemoState } from './types';

const DemoContext = createContext<DemoState>({
  isDemo: false,
  currentStep: null,
  highlightedKey: null,
});

export function useDemo() {
  return useContext(DemoContext);
}

interface DemoMessage {
  type: 'demo:activate' | 'demo:deactivate';
  key?: string;
  step?: DemoStep;
}

export function DemoProvider({ children }: { children: React.ReactNode }) {
  const isDemo = import.meta.env.VITE_DEMO_MODE === 'true';

  useEffect(() => {
    if (isDemo) (window as any).__DEMO_MODE__ = true;
  }, [isDemo]);

  const [currentStep, setCurrentStep] = useState<DemoStep | null>(null);
  const [highlightedKey, setHighlightedKey] = useState<string | null>(null);

  const handleMessage = useCallback((event: MessageEvent<DemoMessage>) => {
    if (!event.data?.type) return;

    if (event.data.type === 'demo:activate') {
      setHighlightedKey(event.data.key ?? null);
      setCurrentStep(event.data.step ?? null);
    } else if (event.data.type === 'demo:deactivate') {
      setHighlightedKey(null);
      setCurrentStep(null);
    }
  }, []);

  useEffect(() => {
    if (!isDemo) return;
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isDemo, handleMessage]);

  if (!isDemo) {
    return <>{children}</>;
  }

  return (
    <DemoContext.Provider value={{ isDemo, currentStep, highlightedKey }}>
      {children}
    </DemoContext.Provider>
  );
}
